import React from 'react'
import Link from 'next/link'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoginSchema } from '@/src/types'
import { PasswordField } from '@/src/components/form-components'
import Button from '@/src/components/button'
import { Box, TextField, Typography } from '@mui/material'
import DOMPurify from 'dompurify'

interface FormData {
  email: string
  password: string
}
export interface Props {
  onSubmit: (formData: FormData) => void
  loading: boolean
  isDisable: boolean
}

const LoginForm = ({ onSubmit, loading, isDisable }: Props) => {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormData>({ resolver: yupResolver(LoginSchema) })

  const sanitizeInput = (data: FormData): FormData => {
    // Use DOMPurify to sanitize the input
    return {
      email: DOMPurify.sanitize(data.email),
      password: DOMPurify.sanitize(data.password)
    }
  }

  const formSubmit = (data: FormData) => {
    const sanitizedData = sanitizeInput(data) // Sanitize form data
    onSubmit(sanitizedData)
  }

  return (
    <div>
      <form
        className="flex flex-col gap-3 max-w-96 min-w-fit max-sm:min-w-0 max-sm:w-[20.438rem] sm:w-[45%]"
        onSubmit={handleSubmit(formSubmit)}
      >
        <TextField
          label={'Email Address'}
          {...register('email')}
          variant="outlined"
          autoComplete="off"
          placeholder={'Email Address'}
          error={!!errors.email}
          helperText={errors.email?.message}
          fullWidth
          InputProps={{
            className: `rounded-[0.625rem] border-icon-darkgray `
          }}
          InputLabelProps={{
            className: 'text-secondary-main'
          }}
        />
        <PasswordField
          id="login-password"
          label="Password"
          placeholder="Password"
          validation={register('password')}
          error={!!errors.password}
          helperText={errors.password?.message}
          InputLabelProps={{
            className: 'text-secondary-main'
          }}
        />
        <Box
          className={`sm:w-[25rem] z-10 flex justify-end ${!!errors.password ? 'mt-0 sm:-mt-9' : '-mt-3'}`}
        >
          <Link
            className={`text-x  text-right  sm:pr-5  text-icon-darkGray font-semibold `}
            data-testid="automation-IRHQ_SignIn-ForgotPassword"
            href="/forgot-password"
          >
            <Typography
              variant="subtitle2"
              color="text.secondary"
              className="!normal-case text-[0.813rem]"
            >
              Forgot password?
            </Typography>
          </Link>
        </Box>
        <Button
          fullwidth={true}
          loading={loading}
          text="Sign in"
          type="submit"
          disabled={isDisable}
          onClick={() => {}}
          extraClass="!normal-case"
        />
      </form>
    </div>
  )
}

export default LoginForm
